import React from 'react';
import { User } from 'lucide-react';
import { getAvatarColor, getReadableColorOnWhite } from '../../utils/colorUtils';
import { formatProviderName, type PreferredProviderOption } from './PreferredProviderDropdown';

interface AssigneeChipProps {
  /** Resolved assignee, or null when nobody is pinned to the slot yet. */
  assignee: PreferredProviderOption | null;
  size?: 'xs' | 'sm';
  className?: string;
}

const initialsOf = (opt: PreferredProviderOption): string => {
  const both = `${(opt.firstName ?? '').trim()[0] ?? ''}${(opt.lastName ?? '').trim()[0] ?? ''}`.toUpperCase();
  if (both) return both;
  return ((opt.name || opt.email || '?')[0] ?? '?').toUpperCase();
};

/**
 * Identity color for an assignee plus a darkened variant safe for small text
 * sitting on a tint of that color. Exported so bigger surfaces (the
 * confirmation hero) can tint themselves to match the slot chips.
 */
export const getAssigneeColors = (assignee: PreferredProviderOption): { color: string; textColor: string } => {
  const color = assignee.color || getAvatarColor(assignee.id || formatProviderName(assignee));
  return { color, textColor: getReadableColorOnWhite(color, 110) };
};

/**
 * Name + photo of the team member a slot belongs to, tinted with that
 * person's stable avatar color.
 *
 * Slot lists routinely mix two or three providers, and when every row reads
 * as the same gray text the operator can pick a slot for the wrong person
 * without noticing. The per-user color is the fast visual signal that the
 * assignee changed between rows; the name is still spelled out for anyone
 * who can't rely on color alone.
 */
export const AssigneeChip: React.FC<AssigneeChipProps> = ({ assignee, size = 'xs', className = '' }) => {
  const text = size === 'xs' ? 'text-[11.5px]' : 'text-[12.5px]';
  const avatar = size === 'xs' ? 'w-4 h-4 text-[7.5px]' : 'w-5 h-5 text-[9px]';

  if (!assignee) {
    return (
      <span className={`inline-flex items-center gap-1 ${text} text-ink-500 ${className}`}>
        <User className="h-3 w-3 text-ink-400" />
        Unassigned
      </span>
    );
  }

  const { color, textColor } = getAssigneeColors(assignee);

  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 rounded-full border ${text} font-semibold ${className}`}
      style={{ color: textColor, backgroundColor: `${color}1F`, borderColor: `${color}59` }}
      title={assignee.email || undefined}
    >
      {assignee.photoUrl ? (
        <img
          src={assignee.photoUrl}
          alt={formatProviderName(assignee)}
          className={`${avatar} rounded-full object-cover flex-shrink-0`}
        />
      ) : (
        <span
          className={`${avatar} rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold leading-none`}
          style={{ backgroundColor: color }}
        >
          {initialsOf(assignee)}
        </span>
      )}
      <span className="truncate">{formatProviderName(assignee)}</span>
    </span>
  );
};

export default AssigneeChip;
